import type { $Enums } from '@prisma/client';

/**
 * Single source of truth for every order status's open/closed disposition (TAS-3542).
 *
 *   web  ─▶ ⌘K `is:open` / `is:closed`   scope filter
 *   jobs ─▶ Typesense `rankScore`        open ranks above closed
 *
 * Both consumers import from here — there is no private copy. The enum-keyed
 * `Record<$Enums.XStatus, boolean>` forces an explicit call for every status:
 * adding a value to a Prisma order-status enum will not compile until it is
 * classified here (a `Set` would silently treat any un-listed status as open).
 * `true` = open/actionable, `false` = closed/inactive.
 */
export const SALES_ORDER_IS_OPEN: Record<$Enums.SalesOrderStatus, boolean> = {
  DRAFT_CUSTOMER: false,
  DRAFT: false,
  PENDING: true,
  SUBMITTED: true,
  APPROVED: true,
  PICKING: true,
  SHIPPED: true,
  CLOSED: false,
  CANCELLED: false,
  ERROR: false,
};

export const PURCHASE_ORDER_IS_OPEN: Record<$Enums.PurchaseOrderStatus, boolean> = {
  DRAFT: false,
  SUBMITTED: true,
  RECEIVING: true,
  SHIPPED: true,
  PENDING_LIQUIDATION: true,
  CLOSED: false,
  CANCELLED: false,
  ERROR: false,
};

export const WORK_ORDER_IS_OPEN: Record<$Enums.WorkOrderStatus, boolean> = {
  DRAFT: false,
  SUBMITTED: true,
  RECEIVING: true,
  PENDING_SETTLEMENT: true,
  CLOSED: false,
  CANCELLED: false,
};

export const BUYSELL_ORDER_IS_OPEN: Record<$Enums.BuySellOrderStatus, boolean> = {
  DRAFT: false,
  SUBMITTED: true,
  PAYMENT_RECONCILIATION: true,
  CLOSED: false,
  CANCELLED: false,
};

/** The status values of `map` whose disposition equals `open`. */
function statusesWhere<Status extends string>(map: Record<Status, boolean>, open: boolean): Status[] {
  return (Object.keys(map) as Status[]).filter((status) => map[status] === open);
}

/** `orderType` discriminator value → its open/closed status sets, for the `is:` scope filter. */
export const ORDER_STATUS_OPEN_STATE: Record<string, { open: string[]; closed: string[] }> = {
  SALES_ORDER: { open: statusesWhere(SALES_ORDER_IS_OPEN, true), closed: statusesWhere(SALES_ORDER_IS_OPEN, false) },
  PURCHASE_ORDER: {
    open: statusesWhere(PURCHASE_ORDER_IS_OPEN, true),
    closed: statusesWhere(PURCHASE_ORDER_IS_OPEN, false),
  },
  WORK_ORDER: { open: statusesWhere(WORK_ORDER_IS_OPEN, true), closed: statusesWhere(WORK_ORDER_IS_OPEN, false) },
  BUYSELL_ORDER: {
    open: statusesWhere(BUYSELL_ORDER_IS_OPEN, true),
    closed: statusesWhere(BUYSELL_ORDER_IS_OPEN, false),
  },
};
